import { Button } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { RefundStatusDisplay } from "./RefundStatusDisplay";
import { VyneTable, type VyneTableProps } from "@vyne-shared/components/vyneTable/VyneTable";
import { dollarsFromBasisPoints, stringFromDollarsShowPositiveSymbol } from "@vyne-shared/utils/currency";
import { formatDateNumerically } from "@vyne-shared/utils/date";
import type { ExpandableConfig } from "antd/es/table/interface";
import type { PatientHistoryResult, RefundResultStatus } from "api/responses";

interface ExpandedRowContent {
    key: string;
    date: Date;
    amount: string;
    type: string;
    status: RefundResultStatus | "writeback";
}

// if transaction has no partial refunds and it wasn't written back to PMS, don't show expand icon
function isRowExpandable(record: PatientHistoryResult): boolean {
    return (!!record.refunds && record.refunds.length > 0) || !!record.activeWriteback;
}

function getExpandedRowContent(record: PatientHistoryResult): ExpandedRowContent[] {
    // map refunds into data for expanded rows
    const expandedRows: ExpandedRowContent[] =
        record.refunds?.map((refund) => ({
            key: `${record.eventId}-Refund-Event-${new Date(refund.refundDate).toISOString()}`,
            date: new Date(refund.refundDate),
            amount: stringFromDollarsShowPositiveSymbol(dollarsFromBasisPoints(refund.amount)),
            type: refund.isPartial ? "Partial Refund" : "Full Refund",
            status: refund.status,
        })) ?? [];

    // transaction was written back to PMS, include that in the expanded rows
    if (record.activeWriteback) {
        expandedRows.push({
            key: `${record.eventId}-Writeback-Event-${new Date(record.activeWriteback.postedDate).toISOString()}`,
            date: new Date(record.activeWriteback.postedDate),
            amount: "",
            type: `Writeback - ${record.activeWriteback.username}`,
            status: "writeback",
        });

        // sort refunds and writeback by date
        expandedRows.sort((a, b) => b.date.getTime() - a.date.getTime());
    }

    return expandedRows;
}

const partialRefundExpandableColumns: VyneTableProps<ExpandedRowContent>["columns"] = [
    {
        title: "Date",
        dataIndex: "date",
        render: (_, row) => (
            <time dateTime={row.date.toISOString()} title={row.date.toLocaleString()}>
                {formatDateNumerically(row.date)}
            </time>
        ),
    },
    {
        title: "Amount",
        dataIndex: "amount",
    },
    {
        title: "Type",
        dataIndex: "type",
    },
    {
        title: "Status",
        dataIndex: "status",
        render: (status: ExpandedRowContent["status"]) =>
            status === "writeback" ? "See Transactions tab" : <RefundStatusDisplay status={status} />,
    },
];

/** Configuration for AntD table to expand history results to show refunds and write-backs. */
export const partialRefundExpandable: ExpandableConfig<PatientHistoryResult> = {
    expandIcon: ({ expanded, onExpand, record }) =>
        isRowExpandable(record) ? (
            <Button
                size="small"
                type="link"
                title="View refunds"
                aria-label="View refunds"
                onClick={(e) => onExpand(record, e)}
            >
                {expanded ? <DownOutlined /> : <RightOutlined />}
            </Button>
        ) : null,
    expandedRowRender: (record) => (
        <VyneTable
            name={`Refund History for Transaction ${record.eventId} on ${record.eventDate}`}
            rowKey="key"
            dataSource={getExpandedRowContent(record)}
            columns={partialRefundExpandableColumns}
            className="refund-history-sub-table"
            data-testid="refund-history-sub-table"
            pagination={false}
            locale={{ emptyText: "No events found." }}
        />
    ),
    rowExpandable: isRowExpandable,
};
